import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Broadcast, Eye, EyeSlash, PencilSimple, PlugsConnected, DownloadSimple, ArrowsCounterClockwise, Power } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_COLORS = {
  BRAS: "text-primary border-primary/40 bg-primary/10",
  OLT: "text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)]",
  AccessPoint: "text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)]",
  Router: "text-muted-foreground border-border bg-secondary",
  Switch: "text-muted-foreground border-border bg-secondary",
  Other: "text-muted-foreground border-border bg-secondary",
};

const emptyForm = {
  name: "", nas_ip: "", shared_secret: "", nas_type: "BRAS",
  vendor: "", location: "", coa_port: 3799, auth_port: 1812, acct_port: 1813, description: "",
  api_user: "", api_password: "", api_port: 8728,
  max_active_users: "",
  default_graph_iface: "",
  force_offline: false,
};

export default function NasInventory() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // full NAS doc when editing
  const [reveal, setReveal] = useState({});
  const [form, setForm] = useState(emptyForm);

  const load = async () => setItems((await http.get("/nas")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      name: n.name || "", nas_ip: n.nas_ip || "", shared_secret: n.shared_secret || "",
      nas_type: n.nas_type || "BRAS", vendor: n.vendor || "", location: n.location || "",
      coa_port: n.coa_port ?? 3799, auth_port: n.auth_port ?? 1812, acct_port: n.acct_port ?? 1813,
      description: n.description || "",
      api_user: n.api_user || "", api_password: n.api_password || "", api_port: n.api_port ?? 8728,
      max_active_users: n.max_active_users ?? "",
      default_graph_iface: n.default_graph_iface || "",
      force_offline: !!n.force_offline,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        coa_port: Number(form.coa_port),
        auth_port: Number(form.auth_port),
        acct_port: Number(form.acct_port),
        api_port: Number(form.api_port),
        max_active_users: form.max_active_users === "" ? null : Number(form.max_active_users),
        default_graph_iface: form.default_graph_iface || "",
        force_offline: !!form.force_offline,
      };
      if (editing) await http.patch(`/nas/${editing.id}`, payload);
      else await http.post("/nas", payload);
      toast.success(editing ? "NAS updated" : "NAS registered");
      setOpen(false); setForm(emptyForm); setEditing(null);
      load();
    } catch (err) {
      // Surface the real reason so operators aren't left staring at a generic error
      const d = err?.response?.data?.detail || err?.response?.data || err?.message;
      toast.error(typeof d === "string" ? d : `Failed to save NAS (HTTP ${err?.response?.status || "?"})`, { duration: 6000 });
      console.error("NAS save error:", err?.response || err);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this NAS?")) return;
    await http.delete(`/nas/${id}`);
    toast.success("NAS removed");
    load();
  };

  const toggleForceOffline = async (n) => {
    // Instant kill-switch for "we removed this NAS from RADIUS on their side".
    // Flipping force_offline=true prunes every ghost session on it and drops
    // its subscribers from the online count within seconds (no need to wait
    // for last_seen to age past the stale window — which won't happen while
    // RouterOS keeps trickling zombie Interim-Update accounting packets).
    const now = !n.force_offline;
    if (now && !confirm(`Mark ${n.name} as disconnected from RADIUS? This immediately closes every active session on it and flips its subscribers to offline. Toggle off later if the router comes back.`)) return;
    try {
      await http.patch(`/nas/${n.id}`, { force_offline: now });
      toast.success(now ? `${n.name} muted — ghost sessions purged` : `${n.name} un-muted — resuming API polling`);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message;
      toast.error(typeof d === "string" ? d : "Failed to toggle NAS state");
    }
  };

  const testApi = async (n) => {
    if (!n.api_user || !n.api_password) {
      toast.error("Add API user & password first (Edit → MikroTik API section)");
      return;
    }
    const t = toast.loading(`Pinging ${n.name} API…`);
    try {
      const { data } = await http.get(`/nas/${n.id}/router-health`);
      toast.dismiss(t);
      if (data.reachable) toast.success(`${n.identity || n.name} reachable · ${data.board_name || "MikroTik"} · uptime ${data.uptime}`);
      else toast.error(`Not reachable: ${data.error || "unknown"}`);
    } catch { toast.dismiss(t); toast.error("Health check failed"); }
  };

  const importSubs = async (n) => {
    if (!n.api_user || !n.api_password) {
      toast.error("Add API user & password on this NAS first (Edit → MikroTik API section)");
      return;
    }
    if (!confirm(`Import every /ppp/secret row from ${n.name} into SNS?\n\nExisting subscribers with the same username under this NAS get their password refreshed. Users disabled on the router or already under another NAS are skipped.`)) return;
    const t = toast.loading(`Reading /ppp/secret from ${n.name}…`);
    try {
      const { data } = await http.post(`/nas/${n.id}/import-subscribers`);
      toast.dismiss(t);
      toast.success(`Imported ${data.imported} new · refreshed ${data.updated_password} · skipped ${data.skipped} (of ${data.total_on_nas} rows)`);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  const syncActive = async (n) => {
    if (!n.api_user || !n.api_password) {
      toast.error("Add API user & password on this NAS first (Edit → MikroTik API section)");
      return;
    }
    const t = toast.loading(`Reading /ppp/active from ${n.name}…`);
    try {
      const { data } = await http.post(`/nas/${n.id}/sync-active-sessions`);
      toast.dismiss(t);
      toast.success(`Adopted ${data.adopted} new · refreshed ${data.refreshed} · closed ${data.closed_orphans} orphans (${data.active_on_nas} live on NAS)`);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Live-session sync failed");
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="nas-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">RADIUS clients</div>
          <h1 className="font-display font-bold text-4xl mt-1">NAS Inventory</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every BRAS, OLT, access-point controller or router that talks to your RADIUS server. Add read-only MikroTik API creds on any device to see live traffic, health and interface pickers on the Dashboard.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-nas-btn" onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90">
              <Plus size={14} weight="bold" className="mr-2" /> Register NAS
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing ? `Edit NAS — ${editing.name}` : "Register RADIUS client"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <F label="Name"><Input required data-testid="nas-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
              <F label="Type">
                <Select value={form.nas_type} onValueChange={(v) => setForm({ ...form, nas_type: v })}>
                  <SelectTrigger data-testid="nas-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["BRAS", "OLT", "AccessPoint", "Router", "Switch", "Other"].map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </F>
              <F label="NAS IP"><Input required data-testid="nas-ip" value={form.nas_ip} onChange={(e) => setForm({ ...form, nas_ip: e.target.value })} placeholder="192.168.100.1" /></F>
              <F label="Shared secret"><Input required data-testid="nas-secret" value={form.shared_secret} onChange={(e) => setForm({ ...form, shared_secret: e.target.value })} placeholder="testing123" /></F>
              <F label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></F>
              <F label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></F>
              <F label="Auth port"><Input type="number" value={form.auth_port} onChange={(e) => setForm({ ...form, auth_port: e.target.value })} /></F>
              <F label="Acct port"><Input type="number" value={form.acct_port} onChange={(e) => setForm({ ...form, acct_port: e.target.value })} /></F>
              <F label="CoA port"><Input type="number" value={form.coa_port} onChange={(e) => setForm({ ...form, coa_port: e.target.value })} /></F>
              <F label="Description" span={2}><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>

              <div className="col-span-2 pt-2 border-t border-border">
                <div className="uppercase-label">MikroTik API (optional — enables live Dashboard widgets)</div>
                <p className="text-[11px] text-muted-foreground mt-1">On RouterOS: <code className="font-mono">/ip service enable api</code> and create a read-only user in <code className="font-mono">/user</code> with group <code className="font-mono">read</code>. Default port is 8728.</p>
              </div>
              <F label="API user"><Input data-testid="nas-api-user" value={form.api_user} onChange={(e) => setForm({ ...form, api_user: e.target.value })} placeholder="read-only user" /></F>
              <F label="API password"><Input type="password" data-testid="nas-api-pass" value={form.api_password} onChange={(e) => setForm({ ...form, api_password: e.target.value })} placeholder="strong password" /></F>
              <F label="API port"><Input type="number" data-testid="nas-api-port" value={form.api_port} onChange={(e) => setForm({ ...form, api_port: e.target.value })} placeholder="8728" /></F>
              <F label="Max active users (blank = unlimited)">
                <Input type="number" min={0} data-testid="nas-max-users" value={form.max_active_users}
                       onChange={(e) => setForm({ ...form, max_active_users: e.target.value })}
                       placeholder="e.g. 500 — RADIUS auth rejects new logins once this many sessions are live on this NAS" />
              </F>
              <F label="Default graph interface (Dashboard)">
                <Input data-testid="nas-graph-iface" value={form.default_graph_iface}
                       onChange={(e) => setForm({ ...form, default_graph_iface: e.target.value })}
                       placeholder="e.g. ether1, sfp-sfpplus1 — pinned as the Dashboard live traffic default" />
              </F>
              <F label="Force offline (skip all API calls)" span={2}>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" data-testid="nas-force-offline" checked={!!form.force_offline}
                         onChange={(e) => setForm({ ...form, force_offline: e.target.checked })} />
                  <span>Treat this NAS as disconnected regardless of RADIUS packets — cron won't sync, live-traffic returns empty, imports no-op. Turn on when a router is down for maintenance so SNS stops hammering it.</span>
                </label>
              </F>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="nas-submit" className="rounded-sm bg-primary hover:bg-primary/90">{editing ? "Save changes" : "Register"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((n) => {
          const shown = reveal[n.id];
          const hasApi = !!(n.api_user && n.api_password);
          return (
            <div key={n.id} className="border border-border bg-card p-5 group hover:border-primary/60 transition-colors" data-testid={`nas-${n.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Broadcast size={18} className="text-primary" />
                  <div>
                    <div className="font-display text-lg font-bold">{n.name}</div>
                    <div className="uppercase-label mt-0.5">{n.vendor || "—"}</div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm ${TYPE_COLORS[n.nas_type] || TYPE_COLORS.Other}`}>{n.nas_type}</span>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <R k="NAS IP" v={<span className="font-mono">{n.nas_ip}</span>} />
                <R k="Location" v={n.location || "—"} />
                <R k="Auth / Acct" v={<span className="font-mono">{n.auth_port} / {n.acct_port}</span>} />
                <R k="CoA" v={<span className="font-mono">{n.coa_port}</span>} />
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Secret</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{shown ? n.shared_secret : "•".repeat(Math.min(12, n.shared_secret?.length || 6))}</span>
                    <button onClick={() => setReveal({ ...reveal, [n.id]: !shown })} className="text-muted-foreground hover:text-primary" data-testid={`nas-reveal-${n.id}`} title={shown ? "Hide" : "Reveal"}>
                      {shown ? <EyeSlash size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <R k="MikroTik API" v={hasApi
                  ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))] text-xs"><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" /> configured · port {n.api_port || 8728}</span>
                  : <span className="text-muted-foreground text-xs italic">not set</span>} />
                <R k="RADIUS status" v={
                  <div className="flex items-center gap-2">
                    {n.force_offline
                      ? <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))] text-xs" data-testid={`nas-radius-${n.id}`}><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--warning))]" /> force offline</span>
                      : n.radius_connected
                        ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))] text-xs" data-testid={`nas-radius-${n.id}`}><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))] animate-pulse" /> RADIUS live · {Math.round(n.radius_minutes_since_seen || 0)}m ago</span>
                        : <span className="inline-flex items-center gap-1 text-destructive text-xs" data-testid={`nas-radius-${n.id}`}><span className="w-1.5 h-1.5 rounded-full bg-destructive" /> no packets {n.radius_minutes_since_seen != null ? `${Math.round(n.radius_minutes_since_seen)}m` : "yet"}</span>
                    }
                    <button
                      onClick={() => toggleForceOffline(n)}
                      data-testid={`nas-force-toggle-${n.id}`}
                      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 transition-colors ${n.force_offline ? "border-[hsl(var(--success))]/50 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10" : "border-destructive/50 text-destructive hover:bg-destructive/10"}`}
                      title={n.force_offline ? "Router is back on our RADIUS — un-mute and resume API polling / accounting" : "Router was removed from our RADIUS on their side — mute it here to instantly purge its ghost sessions"}
                    >
                      <Power size={11} weight="bold" /> {n.force_offline ? "reconnect" : "disconnect"}
                    </button>
                  </div>
                } />
              </div>

              {n.description && <p className="text-xs text-muted-foreground mt-4 border-t border-border pt-3">{n.description}</p>}

              {n.last_api_error && !n.last_api_error.ok && (
                <div className="mt-3 border-t border-border pt-3" data-testid={`nas-api-err-${n.id}`}>
                  <div className="flex items-start gap-2 text-xs">
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-destructive font-medium">Last API attempt failed · {n.last_api_error.op}</div>
                      <div className="text-muted-foreground break-words font-mono text-[11px] mt-0.5">{n.last_api_error.error}</div>
                      <div className="text-muted-foreground/70 mt-0.5">{n.last_api_error.at?.replace("T", " ").replace(/\.\d+.*/, "")} UTC · {n.last_api_error.duration_ms}ms</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-between items-center border-t border-border pt-3">
                <button onClick={() => testApi(n)} data-testid={`nas-test-${n.id}`} className={`text-xs inline-flex items-center gap-1 ${hasApi ? "text-primary hover:text-primary/80" : "text-muted-foreground/50 cursor-not-allowed"}`}>
                  <PlugsConnected size={12} /> test API
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => syncActive(n)}
                    disabled={!hasApi}
                    data-testid={`nas-sync-live-${n.id}`}
                    className={`text-xs inline-flex items-center gap-1 ${hasApi ? "text-primary hover:text-primary/80" : "text-muted-foreground/50 cursor-not-allowed"}`}
                    title={hasApi ? "Adopt any /ppp/active session that SNS doesn't know about (fixes 'online in MikroTik but missing on Statistics')" : "Add MikroTik API creds first"}
                  >
                    <ArrowsCounterClockwise size={12} /> sync live
                  </button>
                  <button
                    onClick={() => importSubs(n)}
                    disabled={!hasApi}
                    data-testid={`nas-import-${n.id}`}
                    className={`text-xs inline-flex items-center gap-1 ${hasApi ? "text-primary hover:text-primary/80" : "text-muted-foreground/50 cursor-not-allowed"}`}
                    title={hasApi ? "Import /ppp/secret rows into SNS subscribers" : "Add MikroTik API creds first"}
                  >
                    <DownloadSimple size={12} /> import subs
                  </button>
                  <button onClick={() => openEdit(n)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`nas-edit-${n.id}`}>
                    <PencilSimple size={12} /> edit
                  </button>
                  <button onClick={() => remove(n.id)} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid={`nas-del-${n.id}`}>
                    <Trash size={12} /> remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground col-span-full">
            No NAS registered yet. Click <b>Register NAS</b> to add your first RADIUS client.
          </div>
        )}
      </div>
    </div>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const R = ({ k, v }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
);
